// src/pages/Admin/VendorApplications/ApplicationDetail.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  Mail,
  Phone,
  MapPin,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  User,
  Calendar,
  Download,
  AlertCircle,
  Info,
  Globe,
  ExternalLink
} from 'lucide-react';
import AddressMiniMapDisplay from '../../../../components/admin/AddressMiniMapDisplay';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';
import DocumentViewer from '../../../../components/admin/DocumentViewer';
import { formatPhoneNumber } from '../../../../utils/phoneFormatter';
import toast from '../../../../utils/toast';
import { adminAPI } from '../../../../api/admin';

// Rejection reasons
const REJECTION_REASONS = [
  { value: 'incomplete_info', label: 'Incomplete Information' },
  { value: 'invalid_documents', label: 'Invalid or Expired Documents' },
  { value: 'business_not_verified', label: 'Business Not Verified' },
  { value: 'duplicate_application', label: 'Duplicate Application' },
  { value: 'fraud_suspicion', label: 'Fraud Suspicion' },
  { value: 'policy_violation', label: 'Policy Violation' },
  { value: 'other', label: 'Other (Please specify)' },
];

const ApplicationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [application, setApplication] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionReasonCustom, setRejectionReasonCustom] = useState('');
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showRejectionErrors, setShowRejectionErrors] = useState(false);


  useEffect(() => {
    fetchApplication();
  }, [id]);

  const fetchApplication = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getVendorApplication(id!);
      setApplication(response.data);
    } catch (error) {
      toast.error('Failed to load application');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    setIsProcessing(true);
    try {
      await adminAPI.approveVendorApplication(id!);
      toast.success('Application approved successfully!');
      setShowApproveModal(false);
      navigate('/dashboard/vendor-applications');
    } catch (error) {
      toast.error('Failed to approve application');
    } finally {
      setIsProcessing(false);
    }
  };

  const isRejectionValid = () => {
    if (!rejectionReason) return false;
    if (rejectionReason === 'other' && !rejectionReasonCustom.trim()) return false;
    return true;
  };

  const handleRejectClick = () => {
    setShowRejectionErrors(true);
    if (!isRejectionValid()) {
      // Show errors but don't open modal
      return;
    }
    // Open the confirmation modal if valid
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    let finalReason = rejectionReason === 'other'
      ? rejectionReasonCustom
      : REJECTION_REASONS.find(r => r.value === rejectionReason)?.label;

    // If include the custom text
    finalReason = `${finalReason}: ${rejectionReasonCustom.trim()}`;

    if (!finalReason || finalReason.trim() === '') {
      toast.error('Please provide a reason for rejection');
      return;
    }
    
    setIsProcessing(true);
    try {
      await adminAPI.rejectVendorApplication(id!, finalReason);
      toast.info('Application rejected');
      setShowRejectModal(false);
      navigate('/dashboard/vendor-applications');
    } catch (error) {
      toast.error('Failed to reject application');
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!application) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Application not found</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Back Button */}
      <button
        onClick={() => navigate('/dashboard/vendor-applications')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Applications
      </button>

      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Building2 className="h-8 w-8 text-gray-400" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">{application.business_name}</h1>
              <p className="text-sm text-gray-500">Applied {new Date(application.created_at).toLocaleDateString()}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={application.verification_status} />
          </div>
        </div>
      </div>

      {/* Application Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Business Info */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Business Information</h3>
            <div className="space-y-3">
              <InfoRow label="Business Name" value={application.business_name} />
              <InfoRow label="Business Email" value={application.business_email} icon={Mail} />
              <InfoRow label="Phone" value={formatPhoneNumber(application.business_phone)} icon={Phone} />
              <InfoRow label="Business Type" value={application.business_type} />
              {application.website && (
                <div className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
                  <Globe className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Website</p>
                    <a
                      href={application.website.match(/^https?:\/\//) ? application.website : `https://${application.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline font-medium truncate inline-flex items-center gap-1"
                    >
                      {application.website.replace(/^https?:\/\//, '')}
                      <ExternalLink className="h-3 w-3 flex-shrink-0" />
                    </a>
                  </div>
                </div>
              )}
              {application.description && <InfoRow label="Description" value={application.description} />}
            </div>
          </div>

          {/* Address - Using the new component */}
          {application.business_address && (
            <AddressMiniMapDisplay
              address={application.business_address}
            />
          )}

          {/* Documents - Using DocumentViewer */}
          {(application.registration_file || application.address_proof) && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Uploaded Documents</h3>
              <div className="space-y-3">
                {application.registration_file && (
                  <DocumentViewer
                    file={application.registration_file}
                    label="Registration Certificate"
                  />
                )}
                {application.address_proof && (
                  <DocumentViewer
                    file={application.address_proof}
                    label="Proof of Address"
                  />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar - Actions */}
        <div className="space-y-6">
          {/* Status Card */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Status</h3>
            <div className="flex items-center gap-2 mb-4">
              <StatusBadge status={application.verification_status} size="lg" />
            </div>
            <div className="text-sm text-gray-500">
              <p>Submitted: {new Date(application.created_at).toLocaleDateString()}</p>
              {application.updated_at && (
                <p>Last updated: {new Date(application.updated_at).toLocaleDateString()}</p>
              )}
            </div>
          </div>

          {/* Actions */}
          {application.verification_status === 'pending' && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Actions</h3>

              {/* Approve */}
              <button
                onClick={() => setShowApproveModal(true)}
                disabled={processing}
                className="w-full mb-3 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <CheckCircle className="h-4 w-4" />
                Approve Application
              </button>

              {/* Reject */}
              <div className="border-t border-gray-200 pt-3">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Rejection Reason <span className="text-red-500">*</span>
                </p>
                <select
                  value={rejectionReason}
                  onChange={(e) => {
                    setRejectionReason(e.target.value);
                    // Clear errors when user selects
                    if (showRejectionErrors) {
                      setShowRejectionErrors(false);
                    }
                  }}
                  className={`w-full border rounded-lg p-2 text-sm focus:ring-2 focus:ring-red-500 ${showRejectionErrors && !rejectionReason ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                >
                  <option value="">Select a reason...</option>
                  {REJECTION_REASONS.map((reason) => (
                    <option key={reason.value} value={reason.value}>
                      {reason.label}
                    </option>
                  ))}
                </select>
                {showRejectionErrors && !rejectionReason && (
                  <div className="mt-1 flex items-center text-sm text-red-600">
                    <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                    Please select a reason
                  </div>
                )}

                <div className="mt-2">
                  {rejectionReason === 'other' ? (
                    <p className="text-sm text-gray-600 mb-1">
                      Please specify the reason <span className="text-red-500">*</span>
                    </p>
                  ) : (
                    <p className="text-sm text-gray-600 mb-1">
                      Rejection reason
                    </p>
                  )
                  }
                  <textarea
                    value={rejectionReasonCustom}
                    onChange={(e) => {
                      setRejectionReasonCustom(e.target.value);
                      if (showRejectionErrors) {
                        setShowRejectionErrors(false);
                      }
                    }}
                    placeholder="Provide a detailed reason for rejection..."
                    rows={3}
                    className={`w-full border rounded-lg p-2 text-sm focus:ring-2 focus:ring-red-500 ${showRejectionErrors && !rejectionReasonCustom.trim()
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-300'
                      }`}
                  />
                  {showRejectionErrors && !rejectionReasonCustom.trim() && (
                    <div className="mt-1 flex items-center text-sm text-red-600">
                      <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                      Please provide a reason
                    </div>
                  )}
                  {rejectionReason != 'other' && (
                    <p className="text-sm text-gray-500 mt-1">
                      {rejectionReason ? `Selected: ${REJECTION_REASONS.find(r => r.value === rejectionReason)?.label}` : 'Select a reason above'}
                    </p>
                  )}
                </div>

                <button
                  onClick={handleRejectClick}
                  disabled={processing}
                  className="w-full mt-3 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <XCircle className="h-4 w-4" />
                  Reject Application
                </button>
              </div>
            </div>
          )}

          {/* Already Reviewed */}
          {application.verification_status !== 'pending' && (
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-6 text-center">
              <p className="text-sm text-gray-500">
                {application.verification_status === 'approved' ? (
                  '✅ This application has been approved'
                ) : (
                  '❌ This application has been rejected'
                )}
              </p>
            </div>
          )}
        </div>
      </div>
      {/* Approve Confirmation Modal */}
      <ConfirmationModal
        isOpen={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        onConfirm={handleApprove}
        title="Approve Application?"
        message={`Are you sure you want to approve "${application.business_name}" as a vendor? They will gain immediate access to the vendor dashboard.`}
        confirmText="Approve"
        cancelText="Cancel"
        type="success"
        isProcessing={isProcessing}
      />

      {/* Reject Confirmation Modal */}
      <ConfirmationModal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        onConfirm={handleReject}
        title="Reject Application?"
        message={`Are you sure you want to reject "${application.business_name}"? 
    ${rejectionReason === 'other' ? `Reason: "${rejectionReasonCustom}"` : `Reason: ${REJECTION_REASONS.find(r => r.value === rejectionReason)?.label}`}
    This action cannot be undone.`}
        confirmText="Reject"
        cancelText="Cancel"
        type="danger"
        isProcessing={isProcessing}
      />
    </div>
  );
};

// Helper Components
const StatusBadge: React.FC<{ status: string; size?: 'sm' | 'lg' }> = ({ status, size = 'sm' }) => {
  const configs = {
    pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock, label: 'Pending' },
    approved: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle, label: 'Approved' },
    rejected: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle, label: 'Rejected' },
  };
  const config = configs[status as keyof typeof configs] || configs.pending;
  const Icon = config.icon;
  const padding = size === 'lg' ? 'px-4 py-2' : 'px-2 py-1';
  const textSize = size === 'lg' ? 'text-sm' : 'text-xs';

  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full ${config.bg} ${config.text} ${padding} ${textSize} font-medium`}>
      <Icon className={`${size === 'lg' ? 'h-4 w-4' : 'h-3 w-3'}`} />
      {config.label}
    </span>
  );
};

const InfoRow: React.FC<{ label: string; value: string; icon?: any }> = ({ label, value, icon: Icon }) => (
  <div className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
    {Icon && <Icon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />}
    <div className="flex-1 min-w-0">
      <p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p>
      <p className="text-sm text-gray-900 font-medium truncate">{value || 'N/A'}</p>
    </div>
  </div>
);

export default ApplicationDetail;